/**
 * Reseller and Freelancer hooks — TanStack Query wrappers for the resellers/freelancers API.
 *
 * Hooks:
 * - useResellers(filters?)      — paginated list of reseller profiles (admin)
 * - useReseller(id)             — single reseller by ID (admin)
 * - useMyResellerProfile()      — current user's own reseller profile (reseller role)
 * - useCreateReseller()         — mutation to create a reseller profile (admin)
 * - useUpdateReseller()         — mutation to update a reseller profile (admin)
 *
 * - useFreelancers(filters?)    — paginated list of freelancer profiles (admin)
 * - useFreelancer(id)           — single freelancer by ID (admin)
 * - useMyFreelancerProfile()    — current user's own freelancer profile (freelance role)
 * - useCreateFreelancer()       — mutation to create a freelancer profile (admin)
 * - useUpdateFreelancer()       — mutation to update a freelancer profile (admin)
 *
 * Note: the API returns snake_case rows matching the DB schema.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, apiPut } from '@/lib/api'

// ── Row types (snake_case — mirrors API response) ──────────────────────────────

export interface ResellerRow {
  id: string
  user_id: string
  company_name: string | null
  white_label: boolean
  commission_type: string
  territorial_exclusivity: boolean
  territory_id: string | null
  contract_status: string
  contract_url: string | null
  zoho_request_id: string | null
  active: boolean
  created_at: string
  updated_at: string
  user_name: string | null
  user_email: string | null
  territory_name: string | null
}

export interface FreelancerRow {
  id: string
  user_id: string
  specialty: string | null
  contract_status: string
  contract_url: string | null
  zoho_request_id: string | null
  active: boolean
  created_at: string
  updated_at: string
  user_name: string | null
  user_email: string | null
}

// ── Filter types ───────────────────────────────────────────────────────────────

export interface ResellerListFilters {
  active?: boolean
  contract_status?: string
  search?: string
  limit?: number
  offset?: number
}

export interface FreelancerListFilters {
  active?: boolean
  contract_status?: string
  search?: string
  limit?: number
  offset?: number
}

// ── DTO types ──────────────────────────────────────────────────────────────────

export interface CreateResellerDTO {
  userId: string
  companyName?: string | null
  whiteLabel?: boolean
  commissionType?: 'percentage' | 'fixed'
  territorialExclusivity?: boolean
  territoryId?: string | null
}

export interface UpdateResellerDTO {
  companyName?: string | null
  whiteLabel?: boolean
  commissionType?: 'percentage' | 'fixed'
  territorialExclusivity?: boolean
  territoryId?: string | null
  contractStatus?: 'draft' | 'in_progress' | 'signed'
  contractUrl?: string | null
  zohoRequestId?: string | null
  active?: boolean
}

export interface CreateFreelancerDTO {
  userId: string
  specialty?: string | null
}

export interface UpdateFreelancerDTO {
  specialty?: string | null
  contractStatus?: 'draft' | 'in_progress' | 'signed'
  contractUrl?: string | null
  zohoRequestId?: string | null
  active?: boolean
}

// ── Response wrappers ─────────────────────────────────────────────────────────

interface ResellersListApiResponse {
  data: ResellerRow[]
}

interface ResellerDetailApiResponse {
  data: ResellerRow
}

interface FreelancersListApiResponse {
  data: FreelancerRow[]
}

interface FreelancerDetailApiResponse {
  data: FreelancerRow
}

// ── Query key factory ──────────────────────────────────────────────────────────

export const resellerKeys = {
  all: ['resellers'] as const,
  list: (filters?: ResellerListFilters) => [...resellerKeys.all, 'list', filters] as const,
  detail: (id: string) => [...resellerKeys.all, 'detail', id] as const,
  me: () => [...resellerKeys.all, 'me'] as const,
}

export const freelancerKeys = {
  all: ['freelancers'] as const,
  list: (filters?: FreelancerListFilters) => [...freelancerKeys.all, 'list', filters] as const,
  detail: (id: string) => [...freelancerKeys.all, 'detail', id] as const,
  me: () => [...freelancerKeys.all, 'me'] as const,
}

// ── Reseller hooks ────────────────────────────────────────────────────────────

/**
 * Fetch a list of all reseller profiles (admin only).
 */
export function useResellers(filters?: ResellerListFilters) {
  const params = new URLSearchParams()
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  if (filters?.contract_status) params.set('contract_status', filters.contract_status)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const path = `/api/v1/resellers${qs ? `?${qs}` : ''}`

  return useQuery<ResellerRow[]>({
    queryKey: resellerKeys.list(filters),
    queryFn: async () => {
      const res = await apiGet<ResellersListApiResponse>(path)
      return res.data
    },
  })
}

/**
 * Fetch a single reseller by ID (admin only).
 * Disabled when id is falsy.
 */
export function useReseller(id: string) {
  return useQuery<ResellerRow>({
    queryKey: resellerKeys.detail(id),
    queryFn: async () => {
      const res = await apiGet<ResellerDetailApiResponse>(`/api/v1/resellers/${id}`)
      return res.data
    },
    enabled: !!id,
  })
}

/**
 * Fetch the current user's own reseller profile.
 * Used by reseller-role users to view their own profile.
 */
export function useMyResellerProfile() {
  return useQuery<ResellerRow>({
    queryKey: resellerKeys.me(),
    queryFn: async () => {
      const res = await apiGet<ResellerDetailApiResponse>('/api/v1/resellers/me')
      return res.data
    },
  })
}

/**
 * Mutation to create a new reseller profile (admin only).
 * Invalidates the resellers list cache on success.
 */
export function useCreateReseller() {
  const queryClient = useQueryClient()

  return useMutation<ResellerRow, Error, CreateResellerDTO>({
    mutationFn: async (data) => {
      const res = await apiPost<ResellerDetailApiResponse>('/api/v1/resellers', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
    },
  })
}

/**
 * Mutation to update a reseller profile (admin only).
 * Invalidates list and detail caches on success.
 */
export function useUpdateReseller() {
  const queryClient = useQueryClient()

  return useMutation<ResellerRow, Error, { id: string; data: UpdateResellerDTO }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<ResellerDetailApiResponse>(`/api/v1/resellers/${id}`, data)
      return res.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: resellerKeys.all })
      queryClient.invalidateQueries({ queryKey: resellerKeys.detail(variables.id) })
    },
  })
}

// ── Freelancer hooks ──────────────────────────────────────────────────────────

/**
 * Fetch a list of all freelancer profiles (admin only).
 */
export function useFreelancers(filters?: FreelancerListFilters) {
  const params = new URLSearchParams()
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  if (filters?.contract_status) params.set('contract_status', filters.contract_status)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit))
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset))
  const qs = params.toString()
  const path = `/api/v1/freelancers${qs ? `?${qs}` : ''}`

  return useQuery<FreelancerRow[]>({
    queryKey: freelancerKeys.list(filters),
    queryFn: async () => {
      const res = await apiGet<FreelancersListApiResponse>(path)
      return res.data
    },
  })
}

/**
 * Fetch a single freelancer by ID (admin only).
 * Disabled when id is falsy.
 */
export function useFreelancer(id: string) {
  return useQuery<FreelancerRow>({
    queryKey: freelancerKeys.detail(id),
    queryFn: async () => {
      const res = await apiGet<FreelancerDetailApiResponse>(`/api/v1/freelancers/${id}`)
      return res.data
    },
    enabled: !!id,
  })
}

/**
 * Fetch the current user's own freelancer profile.
 * Used by freelance-role users to view their own profile.
 */
export function useMyFreelancerProfile() {
  return useQuery<FreelancerRow>({
    queryKey: freelancerKeys.me(),
    queryFn: async () => {
      const res = await apiGet<FreelancerDetailApiResponse>('/api/v1/freelancers/me')
      return res.data
    },
  })
}

/**
 * Mutation to create a new freelancer profile (admin only).
 * Invalidates the freelancers list cache on success.
 */
export function useCreateFreelancer() {
  const queryClient = useQueryClient()

  return useMutation<FreelancerRow, Error, CreateFreelancerDTO>({
    mutationFn: async (data) => {
      const res = await apiPost<FreelancerDetailApiResponse>('/api/v1/freelancers', data)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: freelancerKeys.all })
    },
  })
}

/**
 * Mutation to update a freelancer profile (admin only).
 * Invalidates list and detail caches on success.
 */
export function useUpdateFreelancer() {
  const queryClient = useQueryClient()

  return useMutation<FreelancerRow, Error, { id: string; data: UpdateFreelancerDTO }>({
    mutationFn: async ({ id, data }) => {
      const res = await apiPut<FreelancerDetailApiResponse>(`/api/v1/freelancers/${id}`, data)
      return res.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: freelancerKeys.all })
      queryClient.invalidateQueries({ queryKey: freelancerKeys.detail(variables.id) })
    },
  })
}
